import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Coins, ShieldCheck, ArrowUpRight, AlertCircle, Sparkles, Plus, Minus } from "lucide-react";

const TYPE_META = {
  purchase: { iconKey: "plus", cls: "text-emerald-400" },
  bonus: { iconKey: "spark", cls: "text-[var(--gold)]" },
  vip: { iconKey: "minus", cls: "text-[var(--magenta)]" },
  content_buy: { iconKey: "minus", cls: "text-[var(--magenta)]" },
  content_sale: { iconKey: "plus", cls: "text-emerald-400" },
  expiry: { iconKey: "minus", cls: "text-red-400" },
  admin_adjust: { iconKey: "plus", cls: "text-[var(--cream-soft)]" },
  refund: { iconKey: "plus", cls: "text-emerald-400" },
};

const ICON_MAP = { plus: Plus, minus: Minus, spark: Sparkles };

export default function Wallet() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);

  const localeMap = { no: "nb-NO", sv: "sv-SE", da: "da-DK", en: "en-US" };
  const dateLocale = localeMap[i18n.language] || "nb-NO";

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(dateLocale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const load = async () => {
    const [w, txs] = await Promise.all([
      api.get("/wallet/me").then(r => r.data),
      api.get("/wallet/transactions?limit=100").then(r => r.data),
    ]);
    setWallet(w);
    setTxns(txs);
  };

  useEffect(() => { if (user && user !== false) load(); }, [user?.id]);

  if (!user || user === false) {
    return (
      <Layout>
        <p className="font-fraunces italic text-[var(--cream-soft)]">
          <Link to="/login" className="text-[var(--magenta)]">{t("auth.log_in_btn")}</Link> {t("wallet.login_required")}
        </p>
      </Layout>
    );
  }
  if (!wallet) return <Layout><div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div></Layout>;

  const v = { label: t(`wallet.ver_${wallet.verification_level || 0}`), color: wallet.verification_level > 0 ? "text-emerald-400" : "text-[var(--cream-faint)]" };

  return (
    <Layout withSidebar={false}>
      <div className="max-w-4xl mx-auto">
        <div className="font-meta mb-3">{t("wallet.section_label")}</div>
        <h1 className="font-fraunces italic text-5xl text-[var(--cream)] mb-10 leading-tight">{t("wallet.title")}</h1>

        <div className="border border-[var(--gold)]/30 bg-gradient-to-br from-[var(--gold)]/8 to-transparent p-8 mb-10 relative overflow-hidden" data-testid="wallet-balance-card">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[var(--gold)]/5 rounded-full blur-3xl"></div>
          <div className="relative flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="font-meta text-[var(--gold)] mb-2">{t("wallet.balance")}</div>
              <div className="font-fraunces italic text-6xl text-[var(--cream)] inline-flex items-baseline gap-3">
                <Coins className="h-10 w-10 text-[var(--gold)] self-center" />
                <span className="tabular-nums" data-testid="wallet-balance-amount">{wallet.tokens}</span>
                <span className="font-meta text-base text-[var(--cream-soft)]">{t("wallet.tokens")}</span>
              </div>
              {wallet.expires_in_days != null && wallet.tokens > 0 && (
                <div className="mt-3 font-meta text-[var(--cream-faint)]">
                  {wallet.expires_in_days >= 30
                    ? t("wallet.expires_months", { count: Math.round(wallet.expires_in_days / 30) })
                    : t("wallet.expires_days", { count: wallet.expires_in_days })}
                </div>
              )}
            </div>
            <Link to="/kjop" className="btn-magenta inline-flex items-center gap-2" data-testid="wallet-buy-cta">
              <Coins className="h-4 w-4" /> {t("wallet.buy_tokens")}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          <div className="border border-white/10 p-5" data-testid="verification-card">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className={`h-5 w-5 ${wallet.verification_level > 0 ? "text-emerald-400" : "text-[var(--cream-faint)]"}`} />
              <div className="font-fraunces italic text-xl text-[var(--cream)]">{t("wallet.verification")}</div>
            </div>
            <div className={`font-manrope text-base ${v.color} mb-2`}>{v.label}</div>
            {wallet.verification_level === 0 ? (
              <p className="font-manrope text-sm text-[var(--cream-soft)]"
                 dangerouslySetInnerHTML={{ __html: t("wallet.verify_intro") }} />
            ) : (
              <p className="font-manrope text-sm text-[var(--cream-soft)]">
                {t("wallet.verified_at", { date: wallet.verified_at ? fmtDate(wallet.verified_at) : "" })}
              </p>
            )}
          </div>
          <div className="border border-white/10 p-5">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="h-5 w-5 text-[var(--magenta)]" />
              <div className="font-fraunces italic text-xl text-[var(--cream)]">{t("wallet.vip_title")}</div>
            </div>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mb-3">{t("wallet.vip_intro")}</p>
            <Link to="/vip" className="font-manrope text-xs uppercase tracking-[0.2em] text-[var(--magenta)] hover:text-[var(--cream)] inline-flex items-center gap-1">
              {t("wallet.vip_see")} <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <div className="border-l-4 border-[var(--gold)]/40 bg-white/[0.02] p-5 mb-10">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-[var(--gold)] shrink-0 mt-0.5" />
            <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed"
               dangerouslySetInnerHTML={{ __html: t("wallet.info_note") }} />
          </div>
        </div>

        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-5">{t("wallet.transactions")}</h2>
        {txns.length === 0 ? (
          <p className="font-fraunces italic text-[var(--cream-soft)]">{t("wallet.no_transactions")}</p>
        ) : (
          <ul className="border-y border-white/10 divide-y divide-white/5" data-testid="transactions-list">
            {txns.map((tx) => {
              const meta = TYPE_META[tx.type] || { iconKey: "plus", cls: "text-[var(--cream-soft)]" };
              const Icon = ICON_MAP[meta.iconKey] || ArrowUpRight;
              const label = t(`wallet.tx_${tx.type}`, tx.type);
              return (
                <li key={tx.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className={`h-4 w-4 shrink-0 ${meta.cls}`} />
                    <div className="min-w-0">
                      <div className="font-manrope text-sm text-[var(--cream)]">{label}</div>
                      <div className="font-meta text-[var(--cream-faint)]">{fmtDate(tx.created_at)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-manrope text-base tabular-nums ${tx.delta > 0 ? "text-emerald-400" : "text-[var(--cream)]"}`}>
                      {tx.delta > 0 ? "+" : ""}{tx.delta}
                    </div>
                    <div className="font-meta text-[var(--cream-faint)]">{t("wallet.balance_after", { n: tx.balance_after })}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Layout>
  );
}
